import type { MockMediaScene } from "@/src/common/components/app-mock/media";

export interface MockCommunity {
	name: string;
	slug: string;
	domain: string;
	initial: string;
	memberCount: number;
	memberCountLabel: string;
	description: string;
	createdLabel: string;
}

export const MOCK_COMMUNITY: MockCommunity = {
	name: "Cohort Hub",
	slug: "cohort-hub",
	domain: "cohort-hub.upsquad.us",
	initial: "C",
	memberCount: 1284,
	memberCountLabel: "1.2k",
	description: "Workforce development community connecting job seekers, mentors, and employer partners across the metro area.",
	createdLabel: "Created March 12, 2025",
};

export interface MockSiblingCommunity {
	id: string;
	name: string;
	initials: string;
	color: string;
}

export const MOCK_SIBLING_COMMUNITIES: ReadonlyArray<MockSiblingCommunity> = [
	{ id: "tw", name: "TechWorks Academy", initials: "TW", color: "#7c3aed" },
	{ id: "bm", name: "Building Memphis", initials: "BM", color: "#0f766e" },
];

export type MemberRole = "owner" | "admin" | "moderator" | "member";

export const ROLE_CHIP_LABEL: Record<MemberRole, string | null> = {
	owner: "Super Admin",
	admin: "Admin",
	moderator: "Moderator",
	member: null,
};

type Presence = "online" | "recent" | "offline";

export interface MockPerson {
	id: string;
	name: string;
	shortName: string;
	initials: string;
	avatarIndex: number;
	role: MemberRole;
	title: string;
	company: string;
	groups: ReadonlyArray<string>;
	fields: ReadonlyArray<string>;
	presence: Presence;
	lastActive: string;
}

export const MOCK_PEOPLE: ReadonlyArray<MockPerson> = [
	{
		id: "m1",
		name: "Maya Kim",
		shortName: "Maya K.",
		initials: "MK",
		avatarIndex: 1,
		role: "owner",
		title: "Program Director",
		company: "Cohort Hub",
		groups: ["Staff", "Mentors"],
		fields: ["Education", "Workforce"],
		presence: "online",
		lastActive: "Active now",
	},
	{
		id: "m2",
		name: "Devon Tate",
		shortName: "Devon T.",
		initials: "DT",
		avatarIndex: 0,
		role: "admin",
		title: "Employer Relations Lead",
		company: "Cohort Hub",
		groups: ["Staff", "Employer Partners"],
		fields: ["Logistics", "Skilled Trades"],
		presence: "online",
		lastActive: "Active now",
	},
	{
		id: "m3",
		name: "Jordan Park",
		shortName: "Jordan P.",
		initials: "JP",
		avatarIndex: 2,
		role: "member",
		title: "Forklift Operator Trainee",
		company: "Cohort 4",
		groups: ["Cohort 4", "Job Seekers"],
		fields: ["Logistics"],
		presence: "recent",
		lastActive: "12 min ago",
	},
	{
		id: "m4",
		name: "Riley Long",
		shortName: "Riley L.",
		initials: "RL",
		avatarIndex: 3,
		role: "member",
		title: "Patient Care Technician",
		company: "Metro Health",
		groups: ["Cohort 4", "Job Seekers"],
		fields: ["Healthcare"],
		presence: "recent",
		lastActive: "1 hour ago",
	},
	{
		id: "m5",
		name: "Sam Ortega",
		shortName: "Sam O.",
		initials: "SO",
		avatarIndex: 4,
		role: "moderator",
		title: "Career Coach",
		company: "Cohort Hub",
		groups: ["Mentors", "Cohort 3"],
		fields: ["Education", "Finance"],
		presence: "online",
		lastActive: "Active now",
	},
	{
		id: "m6",
		name: "Alex Rivera",
		shortName: "Alex R.",
		initials: "AR",
		avatarIndex: 5,
		role: "member",
		title: "IT Support Apprentice",
		company: "Cohort 4",
		groups: ["Cohort 4"],
		fields: ["IT & Software"],
		presence: "offline",
		lastActive: "3 hours ago",
	},
	{
		id: "m7",
		name: "Taylor Banks",
		shortName: "Taylor B.",
		initials: "TB",
		avatarIndex: 6,
		role: "admin",
		title: "Operations Manager",
		company: "Cohort Hub",
		groups: ["Staff"],
		fields: ["Finance"],
		presence: "offline",
		lastActive: "6 hours ago",
	},
	{
		id: "m8",
		name: "Casey Nguyen",
		shortName: "Casey N.",
		initials: "CN",
		avatarIndex: 7,
		role: "member",
		title: "Welding Student",
		company: "Cohort 3",
		groups: ["Cohort 3", "Job Seekers"],
		fields: ["Skilled Trades"],
		presence: "offline",
		lastActive: "Yesterday",
	},
];

export const VIEWER_ID = "m1";

const PEOPLE_BY_ID: Record<string, MockPerson> = Object.fromEntries(MOCK_PEOPLE.map((person) => [person.id, person]));

const FALLBACK_PERSON: MockPerson = {
	id: "unknown",
	name: "Member",
	shortName: "Member",
	initials: "M",
	avatarIndex: 0,
	role: "member",
	title: "",
	company: "",
	groups: [],
	fields: [],
	presence: "offline",
	lastActive: "",
};

export function getPerson(id: string): MockPerson {
	return PEOPLE_BY_ID[id] ?? FALLBACK_PERSON;
}

export function getPeople(ids: ReadonlyArray<string>): MockPerson[] {
	return ids.map(getPerson);
}

export const VIEWER = getPerson(VIEWER_ID);

interface MockPollOption {
	label: string;
	percent: number;
}

export interface MockPoll {
	options: ReadonlyArray<MockPollOption>;
	votes: number;
	chosenIndex: number;
}

interface MockPostFile {
	name: string;
	meta: string;
}

interface MockVideoMedia {
	scene: MockMediaScene;
	duration: string;
}

export interface MockFeedPost {
	id: string;
	authorId: string;
	timeAgo: string;
	groups: ReadonlyArray<string>;
	paragraphs: ReadonlyArray<string>;
	images?: ReadonlyArray<MockMediaScene>;
	video?: MockVideoMedia;
	poll?: MockPoll;
	eventId?: string;
	file?: MockPostFile;
	likes: number;
	comments: number;
	pinned?: boolean;
	bookmarked?: boolean;
	liked?: boolean;
}

export const MOCK_FEED_POSTS: ReadonlyArray<MockFeedPost> = [
	{
		id: "p1",
		authorId: "m1",
		timeAgo: "2h ago",
		groups: ["Cohort 4"],
		paragraphs: [
			"Cohort 4 onboarding recap is live. Slides, the skills checklist and the mentor pairing sheet are all in Resources.",
			"Huge thanks to everyone who showed up early to help set up the training floor.",
		],
		images: ["workshop", "group"],
		likes: 24,
		comments: 6,
		pinned: true,
	},
	{
		id: "p2",
		authorId: "m2",
		timeAgo: "5h ago",
		groups: ["Job Seekers", "Cohort 4"],
		paragraphs: [
			"Spots are filling up for the Fall Job Fair. 14 employer partners confirmed so far, and we will run resume reviews on site.",
		],
		eventId: "e1",
		likes: 18,
		comments: 4,
	},
	{
		id: "p3",
		authorId: "m5",
		timeAgo: "Yesterday",
		groups: ["Cohort 4", "Cohort 3"],
		paragraphs: ["Which workshop should we run next month? Vote below, the top pick gets scheduled first."],
		poll: {
			options: [
				{ label: "Interview practice", percent: 46 },
				{ label: "Salary negotiation", percent: 31 },
				{ label: "LinkedIn profile clinic", percent: 23 },
			],
			votes: 52,
			chosenIndex: 0,
		},
		likes: 12,
		comments: 9,
	},
	{
		id: "p4",
		authorId: "m3",
		timeAgo: "Yesterday",
		groups: ["Cohort 4"],
		paragraphs: ["Quick walkthrough of the forklift certification prep station at the training center. Ask me anything about the test."],
		video: { scene: "office", duration: "0:48" },
		likes: 31,
		comments: 11,
		bookmarked: true,
	},
	{
		id: "p5",
		authorId: "m4",
		timeAgo: "2d ago",
		groups: [],
		paragraphs: [
			"Just accepted an offer with Metro Health as a patient care technician. Six months ago I did not even have a resume. Thank you to this community and to Sam for the mock interviews.",
		],
		likes: 57,
		comments: 22,
		liked: true,
	},
	{
		id: "p6",
		authorId: "m7",
		timeAgo: "3d ago",
		groups: ["Job Seekers"],
		paragraphs: ["Updated transportation stipend form attached. Submit it before Friday to be reimbursed for September."],
		file: { name: "transportation-stipend-form.pdf", meta: "PDF · 240 KB" },
		likes: 5,
		comments: 1,
	},
];

export type EventFormat = "online" | "in_person" | "hybrid";

export interface MockCommunityEvent {
	id: string;
	title: string;
	description: string;
	dateLabel: string;
	dateChip: string;
	day: number;
	time: string;
	format: EventFormat;
	location: string;
	price: number;
	spots: number;
	rsvpIds: ReadonlyArray<string>;
	scene: MockMediaScene;
	registered?: boolean;
	host?: boolean;
	past?: boolean;
	recurrence?: "Weekly" | "Monthly";
}

export const EVENT_FORMAT_LABEL: Record<EventFormat, string> = {
	online: "Online",
	in_person: "In person",
	hybrid: "Hybrid",
};

export const MOCK_EVENTS_LIST: ReadonlyArray<MockCommunityEvent> = [
	{
		id: "e2",
		title: "Mentor Office Hours",
		description: "Drop in with any question about your job search, resume or upcoming interviews.",
		dateLabel: "Tomorrow",
		dateChip: "Tomorrow",
		day: 9,
		time: "5:00 PM - 6:00 PM",
		format: "online",
		location: "Zoom",
		price: 0,
		spots: 25,
		rsvpIds: ["m2", "m5", "m7"],
		scene: "coffee",
		recurrence: "Weekly",
	},
	{
		id: "e1",
		title: "Fall Job Fair",
		description: "Meet 14 employer partners hiring now. Bring copies of your resume, on-site reviews available.",
		dateLabel: "Thu, Sep 11",
		dateChip: "In 3 days",
		day: 11,
		time: "10:00 AM - 2:00 PM",
		format: "in_person",
		location: "Downtown Career Center, Hall B",
		price: 0,
		spots: 120,
		rsvpIds: ["m2", "m3", "m4", "m6", "m8"],
		scene: "group",
		registered: true,
	},
	{
		id: "e3",
		title: "Career Day: Healthcare Pathways",
		description: "Panel with Metro Health recruiters, then small-group tours of the simulation lab.",
		dateLabel: "Wed, Sep 17",
		dateChip: "In 9 days",
		day: 17,
		time: "9:00 AM - 12:00 PM",
		format: "hybrid",
		location: "Metro Health Campus + Zoom",
		price: 25,
		spots: 60,
		rsvpIds: ["m3", "m4"],
		scene: "campus",
		host: true,
	},
	{
		id: "e4",
		title: "Resume Clinic",
		description: "Bring a draft, leave with a finished resume reviewed by two mentors.",
		dateLabel: "Wed, Sep 24",
		dateChip: "In 16 days",
		day: 24,
		time: "6:00 PM - 7:30 PM",
		format: "online",
		location: "Zoom",
		price: 0,
		spots: 40,
		rsvpIds: ["m6", "m8", "m2", "m3"],
		scene: "workshop",
	},
];

const EVENTS_BY_ID: Record<string, MockCommunityEvent> = Object.fromEntries(MOCK_EVENTS_LIST.map((event) => [event.id, event]));

export function getEvent(id: string): MockCommunityEvent | undefined {
	return EVENTS_BY_ID[id];
}

type MeetingKind = "one_on_one" | "group";
export type MeetingStatus = "accepted" | "pending" | "declined";

export interface MockCommunityMeeting {
	id: string;
	title: string;
	kind: MeetingKind;
	dayLabel: string;
	time: string;
	format: EventFormat;
	status: MeetingStatus;
	live?: boolean;
	participantIds: ReadonlyArray<string>;
	kicker?: string;
	scene: MockMediaScene;
}

export const MEETING_FORMAT_LABEL: Record<EventFormat, string> = {
	online: "Online (Video)",
	in_person: "In-person",
	hybrid: "Hybrid",
};

export const MOCK_MEETINGS_LIST: ReadonlyArray<MockCommunityMeeting> = [
	{
		id: "mt0",
		title: "Mock interview panel",
		kind: "group",
		dayLabel: "In progress",
		time: "2:00 PM - 3:00 PM",
		format: "online",
		status: "accepted",
		live: true,
		participantIds: ["m5", "m3", "m4", "m8"],
		kicker: "Now",
		scene: "presentation",
	},
	{
		id: "mt1",
		title: "Mentor check-in with Alex",
		kind: "one_on_one",
		dayLabel: "Today",
		time: "3:00 PM - 3:30 PM",
		format: "online",
		status: "accepted",
		participantIds: ["m1", "m6"],
		kicker: "in 25 min",
		scene: "coffee",
	},
	{
		id: "mt2",
		title: "Resume review with Devon",
		kind: "one_on_one",
		dayLabel: "Today",
		time: "4:30 PM - 4:45 PM",
		format: "online",
		status: "pending",
		participantIds: ["m1", "m2"],
		scene: "office",
	},
	{
		id: "mt3",
		title: "Cohort 4 goal-setting workshop",
		kind: "group",
		dayLabel: "Tomorrow",
		time: "10:00 AM - 11:00 AM",
		format: "hybrid",
		status: "accepted",
		participantIds: ["m1", "m3", "m4", "m6", "m8"],
		scene: "workshop",
	},
	{
		id: "mt4",
		title: "Employer partner sync",
		kind: "group",
		dayLabel: "Fri, Sep 12",
		time: "2:00 PM - 2:45 PM",
		format: "in_person",
		status: "accepted",
		participantIds: ["m1", "m7", "m5"],
		scene: "handshake",
	},
];

export interface MockCalendarMonth {
	label: string;
	firstWeekday: number;
	daysInMonth: number;
	today: number;
	meetingDays: ReadonlyArray<number>;
	eventDays: ReadonlyArray<number>;
}

export const MOCK_CALENDAR: MockCalendarMonth = {
	label: "September 2026",
	firstWeekday: 2,
	daysInMonth: 30,
	today: 8,
	meetingDays: [8, 9, 12, 22],
	eventDays: [9, 11, 17, 24],
};

export const WEEKDAY_LABELS: ReadonlyArray<string> = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

interface MockChatMessage {
	id: string;
	senderId: string;
	text: string;
	time: string;
}

export interface MockConversation {
	id: string;
	kind: "dm" | "group";
	personId?: string;
	name: string;
	initials: string;
	lastMessage: string;
	time: string;
	unread?: number;
	online?: boolean;
	messages: ReadonlyArray<MockChatMessage>;
}

export const MOCK_CONVERSATIONS: ReadonlyArray<MockConversation> = [
	{
		id: "c1",
		kind: "dm",
		personId: "m2",
		name: "Devon Tate",
		initials: "DT",
		lastMessage: "Sending the employer list now",
		time: "10:32 AM",
		unread: 2,
		online: true,
		messages: [
			{ id: "c1-1", senderId: "m2", text: "Morning! Do you have the updated employer list for the job fair?", time: "10:24 AM" },
			{ id: "c1-2", senderId: "m1", text: "Yes, 14 confirmed. Two more pending from the logistics side.", time: "10:26 AM" },
			{ id: "c1-3", senderId: "m2", text: "Perfect. Can we add resume reviews at the front table?", time: "10:28 AM" },
			{ id: "c1-4", senderId: "m1", text: "Already on it. Sam volunteered for the first shift.", time: "10:30 AM" },
			{ id: "c1-5", senderId: "m2", text: "Sending the employer list now", time: "10:32 AM" },
		],
	},
	{
		id: "c2",
		kind: "group",
		name: "Cohort 4",
		initials: "C4",
		lastMessage: "Riley: Just submitted mine",
		time: "9:21 AM",
		unread: 1,
		messages: [
			{ id: "c2-1", senderId: "m1", text: "Cohort 4, homework is due Friday. The link is in Resources.", time: "9:12 AM" },
			{ id: "c2-2", senderId: "m6", text: "On it", time: "9:14 AM" },
			{ id: "c2-3", senderId: "m4", text: "Just submitted mine", time: "9:21 AM" },
		],
	},
	{
		id: "c3",
		kind: "dm",
		personId: "m5",
		name: "Sam Ortega",
		initials: "SO",
		lastMessage: "Mentor sync at 4pm, agenda in the doc.",
		time: "Yesterday",
		online: true,
		messages: [
			{ id: "c3-1", senderId: "m5", text: "Mentor sync at 4pm, agenda in the doc.", time: "Yesterday" },
			{ id: "c3-2", senderId: "m1", text: "Adding two items about the fall job fair.", time: "Yesterday" },
		],
	},
	{
		id: "c4",
		kind: "group",
		name: "Mentors",
		initials: "ME",
		lastMessage: "Devon: Adding two items, see you",
		time: "Yesterday",
		messages: [
			{ id: "c4-1", senderId: "m5", text: "Mentor sync at 4pm, agenda in the doc.", time: "Yesterday" },
			{ id: "c4-2", senderId: "m2", text: "Adding two items, see you", time: "Yesterday" },
		],
	},
	{
		id: "c5",
		kind: "dm",
		personId: "m3",
		name: "Jordan Park",
		initials: "JP",
		lastMessage: "Thanks for the intro to Devon!",
		time: "Mon",
		messages: [{ id: "c5-1", senderId: "m3", text: "Thanks for the intro to Devon!", time: "Mon" }],
	},
	{
		id: "c6",
		kind: "dm",
		personId: "m8",
		name: "Casey Nguyen",
		initials: "CN",
		lastMessage: "See you at the job fair",
		time: "Sun",
		messages: [{ id: "c6-1", senderId: "m8", text: "See you at the job fair", time: "Sun" }],
	},
];

interface MockAiMetric {
	label: string;
	value: string;
	delta?: string;
}

interface MockAiInsight {
	type: string;
	text: string;
}

export interface MockAiAnswer {
	id: string;
	question: string;
	answer: string;
	metrics: ReadonlyArray<MockAiMetric>;
	insights: ReadonlyArray<MockAiInsight>;
	bars?: ReadonlyArray<{ label: string; value: number }>;
	sql: string;
	meta: string;
}

export const MOCK_AI_ANSWERS: ReadonlyArray<MockAiAnswer> = [
	{
		id: "growth",
		question: "How many members joined this month?",
		answer:
			"27 members joined in September so far, up 18% from the same period in August. Most came through the Cohort 4 onboarding link, and 19 of them have already posted or joined a meeting.",
		metrics: [
			{ label: "New members", value: "27", delta: "+18%" },
			{ label: "Activated", value: "19", delta: "70%" },
			{ label: "Invites pending", value: "8" },
		],
		insights: [
			{ type: "Trend", text: "Growth is concentrated in the second week, right after the Cohort 4 kickoff." },
			{ type: "Opportunity", text: "8 invitations are still pending. Reminders have converted about 40% historically." },
			{ type: "Risk", text: "3 new members have not logged in since joining. A welcome DM usually helps." },
		],
		bars: [
			{ label: "W1", value: 4 },
			{ label: "W2", value: 12 },
			{ label: "W3", value: 7 },
			{ label: "W4", value: 4 },
		],
		sql: "SELECT date_trunc('week', joined_at) AS week, count(*)\nFROM community_members\nWHERE community_id = $1\n  AND joined_at >= date_trunc('month', now())\nGROUP BY 1 ORDER BY 1;",
		meta: "4 rows • Ran in 212 ms • gpt-4.1",
	},
	{
		id: "posts",
		question: "What are the top 5 most-liked posts last week?",
		answer:
			"Riley Long's job offer announcement leads with 57 likes and 22 comments. Success stories and event announcements took four of the five top spots.",
		metrics: [
			{ label: "Total likes", value: "214", delta: "+22%" },
			{ label: "Top post", value: "57 likes" },
			{ label: "Comments", value: "71" },
		],
		insights: [
			{ type: "Pattern", text: "Member success stories earn 2.4x more comments than staff announcements." },
			{ type: "Suggestion", text: "Pin the Fall Job Fair post: it is the most shared post that is not yet pinned." },
		],
		bars: [
			{ label: "Riley", value: 57 },
			{ label: "Jordan", value: 31 },
			{ label: "Maya", value: 24 },
			{ label: "Devon", value: 18 },
			{ label: "Sam", value: 12 },
		],
		sql: "SELECT p.id, p.author_id, count(l.id) AS likes\nFROM posts p LEFT JOIN post_likes l ON l.post_id = p.id\nWHERE p.community_id = $1 AND p.created_at >= now() - interval '7 days'\nGROUP BY p.id ORDER BY likes DESC LIMIT 5;",
		meta: "5 rows • Ran in 184 ms • gpt-4.1",
	},
	{
		id: "events",
		question: "How many events are scheduled in the next 30 days?",
		answer: "4 events are scheduled in the next 30 days with 118 registrations so far. The Fall Job Fair accounts for 74 of them.",
		metrics: [
			{ label: "Upcoming events", value: "4" },
			{ label: "Registrations", value: "118", delta: "+31%" },
			{ label: "Avg. fill rate", value: "62%" },
		],
		insights: [
			{ type: "Capacity", text: "The Fall Job Fair is at 62% capacity with 3 days to go. Last year it sold out 48 hours before." },
			{ type: "Suggestion", text: "Resume Clinic has 6 registrations. A reminder to Job Seekers usually doubles sign-ups." },
		],
		sql: "SELECT e.title, e.starts_at, count(r.id) AS registrations\nFROM events e LEFT JOIN event_registrations r ON r.event_id = e.id\nWHERE e.community_id = $1 AND e.starts_at BETWEEN now() AND now() + interval '30 days'\nGROUP BY e.id ORDER BY e.starts_at;",
		meta: "4 rows • Ran in 96 ms • gpt-4.1",
	},
	{
		id: "monthly",
		question: "Show member growth per month for the last 6 months.",
		answer:
			"Membership grew every month since April, from 12 new members to 27 in September. The two cohort kickoffs (June and September) are the clear peaks.",
		metrics: [
			{ label: "6-month total", value: "110" },
			{ label: "Best month", value: "Sep (27)" },
			{ label: "Avg. per month", value: "18" },
		],
		insights: [
			{ type: "Trend", text: "Kickoff months bring roughly 2x the baseline. Consider a mid-cycle open house to smooth the curve." },
		],
		bars: [
			{ label: "Apr", value: 12 },
			{ label: "May", value: 15 },
			{ label: "Jun", value: 21 },
			{ label: "Jul", value: 14 },
			{ label: "Aug", value: 21 },
			{ label: "Sep", value: 27 },
		],
		sql: "SELECT date_trunc('month', joined_at) AS month, count(*)\nFROM community_members\nWHERE community_id = $1 AND joined_at >= now() - interval '6 months'\nGROUP BY 1 ORDER BY 1;",
		meta: "6 rows • Ran in 143 ms • gpt-4.1",
	},
];

export interface MockAnalyticsRow {
	name: string;
	value: number;
	color: string;
}

interface MockMonthlyActive {
	label: string;
	desktop: number;
	mobile: number;
}

export interface MockAnalytics {
	totalMembers: number;
	activeMembers: number;
	pendingInvites: number;
	oneOnOneMeetings: number;
	groupMeetings: number;
	totalPosts: number;
	totalEvents: number;
	groups: ReadonlyArray<MockAnalyticsRow>;
	popularPosts: ReadonlyArray<MockAnalyticsRow>;
	monthly: ReadonlyArray<MockMonthlyActive>;
}

export const MOCK_ANALYTICS: MockAnalytics = {
	totalMembers: 1284,
	activeMembers: 812,
	pendingInvites: 37,
	oneOnOneMeetings: 96,
	groupMeetings: 41,
	totalPosts: 1930,
	totalEvents: 43,
	groups: [
		{ name: "Job Seekers", value: 486, color: "#22c55e" },
		{ name: "Cohort 4", value: 212, color: "#ef4444" },
		{ name: "Cohort 3", value: 190, color: "#a855f7" },
		{ name: "Employer Partners", value: 64, color: "#f59e0b" },
		{ name: "Mentors", value: 38, color: "#facc15" },
	],
	popularPosts: [
		{ name: "Fall Job Fair announcement", value: 2410, color: "#22c55e" },
		{ name: "Cohort 4 onboarding recap", value: 1980, color: "#ef4444" },
		{ name: "Riley's offer at Metro Health", value: 1720, color: "#a855f7" },
		{ name: "Forklift prep walkthrough", value: 1490, color: "#f59e0b" },
		{ name: "Next workshop poll", value: 1210, color: "#facc15" },
	],
	monthly: [
		{ label: "Oct", desktop: 210, mobile: 260 },
		{ label: "Nov", desktop: 240, mobile: 300 },
		{ label: "Dec", desktop: 190, mobile: 280 },
		{ label: "Jan", desktop: 260, mobile: 340 },
		{ label: "Feb", desktop: 280, mobile: 360 },
		{ label: "Mar", desktop: 310, mobile: 390 },
		{ label: "Apr", desktop: 300, mobile: 420 },
		{ label: "May", desktop: 330, mobile: 450 },
		{ label: "Jun", desktop: 360, mobile: 510 },
		{ label: "Jul", desktop: 340, mobile: 480 },
		{ label: "Aug", desktop: 370, mobile: 530 },
		{ label: "Sep", desktop: 390, mobile: 560 },
	],
};

export interface MockVideoClip {
	id: string;
	authorId: string;
	scene: MockMediaScene;
	duration: string;
	caption: string;
	likes: number;
	comments: number;
	groups: ReadonlyArray<string>;
}

export const MOCK_VIDEOS: ReadonlyArray<MockVideoClip> = [
	{
		id: "v1",
		authorId: "m3",
		scene: "office",
		duration: "0:48",
		caption: "Forklift certification prep station walkthrough. Ask me anything about the test.",
		likes: 128,
		comments: 14,
		groups: ["Cohort 4"],
	},
	{
		id: "v2",
		authorId: "m4",
		scene: "campus",
		duration: "0:32",
		caption: "First day at Metro Health. Six months from no resume to this.",
		likes: 342,
		comments: 41,
		groups: ["Job Seekers"],
	},
	{
		id: "v3",
		authorId: "m5",
		scene: "presentation",
		duration: "1:04",
		caption: "3 questions you will get in every interview, and how to answer them.",
		likes: 96,
		comments: 9,
		groups: ["Mentors"],
	},
	{
		id: "v4",
		authorId: "m8",
		scene: "workshop",
		duration: "0:21",
		caption: "Welding lab, week 3. Getting cleaner beads every day.",
		likes: 77,
		comments: 6,
		groups: ["Cohort 3"],
	},
];

export interface MockMatchSuggestion {
	id: string;
	personId: string;
	score: number;
	reasons: ReadonlyArray<string>;
}

export const MOCK_MATCHES: ReadonlyArray<MockMatchSuggestion> = [
	{ id: "x1", personId: "m5", score: 92, reasons: ["Career coach in Healthcare", "Mentored 3 Cohort 4 members", "Available Thursdays"] },
	{ id: "x2", personId: "m2", score: 87, reasons: ["Employer relations in Logistics", "Same goal: job placement", "Active this week"] },
	{ id: "x3", personId: "m4", score: 81, reasons: ["Hired at Metro Health in August", "Same field: Healthcare", "Cohort 4 peer"] },
];
